/**
 * Where the bearer tokens live.
 *
 * Both tokens are persisted to localStorage. That is a deliberate trade-off: this is an
 * installable PWA that has to keep working after the OS kills the tab, so keeping the
 * refresh token in memory only would sign people out every time they put their phone in
 * a pocket — the exact moment the app is most useful. An httpOnly cookie would be safer
 * against XSS, but the app is served from a different origin to the API in development
 * and would need CSRF handling on every mutation. We accept localStorage and treat
 * "no untrusted HTML is ever rendered" as the invariant that protects it.
 */

const ACCESS_TOKEN_KEY = 'bnb.accessToken';
const REFRESH_TOKEN_KEY = 'bnb.refreshToken';
const EXPIRES_AT_KEY = 'bnb.expiresAt';

export interface TokenSet {
  accessToken: string;
  refreshToken: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

type Listener = (tokens: TokenSet | null) => void;

const listeners = new Set<Listener>();
let cached: TokenSet | null = read();

function read(): TokenSet | null {
  try {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const expiresAt = Number(localStorage.getItem(EXPIRES_AT_KEY) ?? '0');

    if (!accessToken || !refreshToken) {
      return null;
    }

    return { accessToken, refreshToken, expiresAt };
  } catch {
    // Private browsing modes can throw on storage access. Signed out is a safe default.
    return null;
  }
}

export function getTokens(): TokenSet | null {
  return cached;
}

export function setTokens(tokens: TokenSet | null): void {
  cached = tokens;

  try {
    if (tokens) {
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      localStorage.setItem(EXPIRES_AT_KEY, String(tokens.expiresAt));
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(EXPIRES_AT_KEY);
    }
  } catch {
    // Non-fatal: the session still works for as long as this tab is open.
  }

  for (const listener of listeners) {
    listener(tokens);
  }
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Treats a token as expired slightly early, to avoid racing the server clock. */
export function isExpired(tokens: TokenSet, skewMs = 30_000): boolean {
  return tokens.expiresAt - skewMs <= Date.now();
}

/** Shape returned by the Identity login and refresh endpoints. */
export interface AuthTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

export function toTokenSet(response: AuthTokenResponse): TokenSet {
  return {
    accessToken: response.accessToken,
    refreshToken: response.refreshToken,
    expiresAt: Date.now() + response.expiresIn * 1000,
  };
}
